import { useEffect, useMemo, useRef } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// useEditorShortcuts — a single, declarative keyboard-shortcut engine shared by
// BOTH editors (book + manga). One `keydown` listener is attached to the
// document; every registered shortcut is matched against the event with a
// cross-platform combo parser (`mod` = Ctrl on Win/Linux, Cmd/Meta on macOS).
//
// Robustness guarantees baked in here (so callers never re-implement them):
//   • Context guard — a SINGLE-key shortcut (no mod/alt) NEVER fires while the
//     user is typing in an <input>/<textarea>/<select>/contentEditable, nor
//     while a modal dialog ([role=dialog|alertdialog][data-state=open] or
//     [aria-modal=true]) is open. This is what stops "B" from spawning a bubble
//     mid-sentence in the script panel.
//   • `mod`/`alt` combos bypass the typing guard by default (Ctrl+S must work
//     while writing) — unless the shortcut opts into `blockInInput` (undo/redo
//     want the browser's native text-undo while a field is focused).
//   • Selective `preventDefault` (on by default; opt out per shortcut).
//   • The SAME array feeds the help overlay (see `groupShortcuts`), so the
//     documentation can never drift from the actual bindings.
// ─────────────────────────────────────────────────────────────────────────────

export type ShortcutGroup = 'create' | 'navigate' | 'edit' | 'global' | 'help';

export interface EditorShortcut {
  /** Stable id — handy for React keys in the help overlay. */
  id: string;
  /**
   * Combo string (or list of equivalent combos). Tokens joined by '+':
   *   'mod' → Ctrl/Cmd · 'shift' · 'alt' plus one key: a letter ('b'),
   *   a digit, punctuation ('[', ']', '?', '/'), or a named key
   *   ('up','down','left','right','enter','esc','delete','backspace','space').
   * Examples: 'b', 'mod+e', 'mod+shift+g', 'alt+up', 'shift+up', '?'.
   */
  combo: string | string[];
  handler: (e: KeyboardEvent) => void;
  /** Human-readable label for the help overlay (already translated). */
  description: string;
  group: ShortcutGroup;
  /** Optional extra guard — the shortcut only fires when this returns true. */
  when?: () => boolean;
  /** Fire even while an input/textarea/contentEditable is focused. */
  allowInInput?: boolean;
  /** Force the typing guard even for mod/alt combos (e.g. undo → native undo). */
  blockInInput?: boolean;
  /** Fire even while a modal dialog is open (the help overlay's own toggle). */
  allowInModal?: boolean;
  /** Call `preventDefault()` when it fires (default: true). */
  preventDefault?: boolean;
  /** Set false to register-but-disable without unmounting the whole set. */
  enabled?: boolean;
}

interface ParsedCombo {
  mod: boolean;
  shift: boolean;
  alt: boolean;
  key: string;
}

const NAMED_KEYS = new Set([
  'up', 'down', 'left', 'right', 'enter', 'esc', 'delete', 'backspace',
  'space', 'tab', 'home', 'end', 'pageup', 'pagedown',
]);

function parseCombo(combo: string): ParsedCombo {
  const parts = combo.toLowerCase().trim().split('+').map((p) => p.trim());
  const mod = parts.includes('mod') || parts.includes('ctrl') || parts.includes('cmd') || parts.includes('meta');
  const shift = parts.includes('shift');
  const alt = parts.includes('alt') || parts.includes('option');
  const key = parts.filter((p) => !['mod', 'ctrl', 'cmd', 'meta', 'shift', 'alt', 'option'].includes(p)).join('+');
  return { mod, shift, alt, key };
}

/** Normalize a KeyboardEvent's `key` into our combo vocabulary. */
function normalizeEventKey(e: KeyboardEvent): string {
  const k = e.key;
  switch (k) {
    case ' ': return 'space';
    case 'ArrowUp': return 'up';
    case 'ArrowDown': return 'down';
    case 'ArrowLeft': return 'left';
    case 'ArrowRight': return 'right';
    case 'Escape': return 'esc';
    case 'Delete': return 'delete';
    case 'Backspace': return 'backspace';
    case 'Enter': return 'enter';
    case 'Tab': return 'tab';
    case 'Home': return 'home';
    case 'End': return 'end';
    case 'PageUp': return 'pageup';
    case 'PageDown': return 'pagedown';
    default: return k.toLowerCase();
  }
}

function matchesEvent(parsed: ParsedCombo, e: KeyboardEvent): boolean {
  const isMod = e.ctrlKey || e.metaKey;
  if (parsed.mod !== isMod) return false;
  if (parsed.alt !== e.altKey) return false;

  const eventKey = normalizeEventKey(e);
  if (eventKey !== parsed.key) return false;

  // Shift is enforced for letters and named keys (so 'b' ≠ Shift+B, and
  // 'up' ≠ 'shift+up'). For symbol keys it is NOT enforced, because the symbol
  // itself already encodes the shift state ('?' is Shift+'/', '[' is unshifted)
  // and browsers vary in what `shiftKey` reports for them.
  const enforceShift = /^[a-z]$/.test(parsed.key) || NAMED_KEYS.has(parsed.key);
  if (enforceShift && parsed.shift !== e.shiftKey) return false;

  return true;
}

function isEditableTarget(e: KeyboardEvent): boolean {
  const el = (e.target as HTMLElement | null) ?? (document.activeElement as HTMLElement | null);
  if (!el) return false;
  const tag = el.tagName?.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (el.isContentEditable) return true;
  return false;
}

function isModalOpen(): boolean {
  return !!document.querySelector(
    '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [aria-modal="true"]',
  );
}

/** Register a set of shortcuts. The array may be rebuilt every render — an
 * internal ref keeps the single listener pointed at the latest handlers, so
 * closures always see fresh state without re-attaching the listener. */
export function useEditorShortcuts(shortcuts: EditorShortcut[], options?: { enabled?: boolean }): void {
  const ref = useRef<EditorShortcut[]>(shortcuts);
  const enabledRef = useRef<boolean>(options?.enabled ?? true);
  useEffect(() => { ref.current = shortcuts; });
  useEffect(() => { enabledRef.current = options?.enabled ?? true; });

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!enabledRef.current) return;
      // Ignore lone modifier presses.
      if (e.key === 'Control' || e.key === 'Meta' || e.key === 'Shift' || e.key === 'Alt') return;

      const editable = isEditableTarget(e);
      const modalOpen = isModalOpen();

      for (const s of ref.current) {
        if (s.enabled === false) continue;

        const combos = Array.isArray(s.combo) ? s.combo : [s.combo];
        if (!combos.some((c) => matchesEvent(parseCombo(c), e))) continue;

        if (s.when && !s.when()) continue;

        const parsed = parseCombo(combos[0]);
        const bypassTyping = !s.blockInInput && (s.allowInInput || parsed.mod || parsed.alt);
        if (editable && !bypassTyping) continue;

        if (modalOpen && !s.allowInModal) continue;

        if (s.preventDefault !== false) e.preventDefault();
        s.handler(e);
        return; // first match wins
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);
}

// ── Help-overlay helpers ─────────────────────────────────────────────────────

const IS_MAC = typeof navigator !== 'undefined' && /mac|iphone|ipad|ipod/i.test(navigator.platform || navigator.userAgent);

const KEY_LABELS: Record<string, string> = {
  up: '↑', down: '↓', left: '←', right: '→',
  enter: '⏎', esc: 'Esc', delete: 'Del', backspace: '⌫',
  space: 'Espaço', tab: 'Tab', pageup: 'PgUp', pagedown: 'PgDn',
};

/** Pretty, platform-aware label parts for a single combo string. */
export function formatCombo(combo: string): string[] {
  const parsed = parseCombo(combo);
  const parts: string[] = [];
  if (parsed.mod) parts.push(IS_MAC ? '⌘' : 'Ctrl');
  if (parsed.alt) parts.push(IS_MAC ? '⌥' : 'Alt');
  if (parsed.shift) parts.push(IS_MAC ? '⇧' : 'Shift');
  const key = parsed.key;
  parts.push(KEY_LABELS[key] ?? (key.length === 1 ? key.toUpperCase() : key));
  return parts;
}

export interface ShortcutHelpItem {
  id: string;
  /** All equivalent combos, each pre-split into pretty key parts. */
  combos: string[][];
  description: string;
}

export interface ShortcutHelpSection {
  group: ShortcutGroup;
  /** Optional label override (App uses it for the "Global" app-nav section). */
  title?: string;
  items: ShortcutHelpItem[];
}

const GROUP_ORDER: ShortcutGroup[] = ['create', 'navigate', 'edit', 'global', 'help'];

/** Turns the registered shortcut list into grouped, display-ready rows for the
 * help overlay — the single source of truth for both behaviour and docs. */
export function groupShortcuts(shortcuts: EditorShortcut[]): ShortcutHelpSection[] {
  const byGroup = new Map<ShortcutGroup, ShortcutHelpItem[]>();
  for (const s of shortcuts) {
    if (s.enabled === false) continue;
    const combos = (Array.isArray(s.combo) ? s.combo : [s.combo]).map(formatCombo);
    const list = byGroup.get(s.group) ?? [];
    list.push({ id: s.id, combos, description: s.description });
    byGroup.set(s.group, list);
  }
  return GROUP_ORDER
    .filter((g) => byGroup.has(g))
    .map((group) => ({ group, items: byGroup.get(group)! }));
}

/** Convenience hook: memo-group a shortcut list for the overlay. */
export function useShortcutSections(shortcuts: EditorShortcut[]): ShortcutHelpSection[] {
  // Group whenever the *shape* changes; ids+descriptions are a cheap key.
  const key = shortcuts.map((s) => `${s.id}:${s.enabled === false ? 0 : 1}`).join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => groupShortcuts(shortcuts), [key]);
}
